import React, { useState } from "react";
import "./Tabs.css";

type TabsProps = {
  defaultValue: string;
  children: React.ReactNode;
  className?: string;
};

const TabsContext = React.createContext<{
  value: string;
  setValue: (v: string) => void;
} | null>(null);

const Tabs = ({ defaultValue, children, className }: TabsProps) => {
  const [value, setValue] = useState(defaultValue);

  return (
    <TabsContext.Provider value={{ value, setValue }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
};

const TabsList = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={`tabs-list ${className || ""}`} {...props} />
  )
);

type TabsTriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  value: string;
};

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ className, value, ...props }, ref) => {
    const ctx = React.useContext(TabsContext);
    if (!ctx) throw new Error("TabsTrigger must be used inside <Tabs>");
    const active = ctx.value === value;

    return (
      <button
        ref={ref}
        data-state={active ? "active" : "inactive"}
        onClick={() => ctx.setValue(value)}
        className={`tabs-trigger ${className || ""}`}
        {...props}
      />
    );
  }
);

type TabsContentProps = React.HTMLAttributes<HTMLDivElement> & {
  value: string;
};

const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
  ({ className, value, ...props }, ref) => {
    const ctx = React.useContext(TabsContext);
    if (!ctx) throw new Error("TabsContent must be used inside <Tabs>");
    if (ctx.value !== value) return null;

    return (
      <div ref={ref} className={`tabs-content ${className || ""}`} {...props} />
    );
  }
);

export { Tabs, TabsList, TabsTrigger, TabsContent };
